import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { DimmedBackground } from '../../../common/dimmed/DimmedBackground';
import { Icon } from '../../../common/icon/Icon';

interface SlideModalSmootherProps {
  targetState: boolean;
  openDelayTime: number;
  height?: string;
  maxHeight?: string;
  hasRoundBorder?: boolean;
  isShowCloseButton?: boolean;
  handleCloseButtonClick?: () => void;
  children: React.ReactNode;
  padding?: string;
  gap?: string;
}

export const SlideModalSmoother = ({
  targetState,
  openDelayTime = 0,
  height = '30%',
  maxHeight = '95%',
  hasRoundBorder = true,
  isShowCloseButton = true,
  handleCloseButtonClick,
  children,
  padding,
  gap,
}: SlideModalSmootherProps) => {
  // 모달을 Open 하기 위해 존재하는 State, targetState의 값에 따라 값이 변경됨
  const [isSlideModalOpen, setIsSlideModalOpen] = useState<boolean>(false);

  // 모달의 Y 좌표 값, 아래에서 위로 나타나도록 하기 위해 초기 Y 좌표 값을 초기에는 100% 로 설정
  const [modalPositionY, setModalPositionY] = useState<string>('100%');

  // 모달을 Open할 때는, openDelayTime 이후에 모달 open animate와 상태를 같이 변경
  const handleModalOpen = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsSlideModalOpen(true);
      animateModalOpen();
    }, openDelayTime);

    return () => clearTimeout(timeout);
  }, [openDelayTime]);

  // 모달을 Close할 때는, 먼저 모달을 내린 후, 300ms (모달이 다 내려가는데 걸리는 시간) 이후 handleCloseButtonClick 메서드를 수행한다.
  const handleModalClose = useCallback(() => {
    animateModalClose();

    const timeout = setTimeout(() => {
      setIsSlideModalOpen(false);
    }, 400);

    return () => clearTimeout(timeout);
  }, []);

  // 모달을 Open 할 때의 애니메이션
  const animateModalOpen = () => {
    setTimeout(() => {
      setModalPositionY('0%');
    }, 100);
  };

  // 모달을 Close 할 때의 애니메이션
  const animateModalClose = () => {
    setTimeout(() => {
      setModalPositionY('100%');
    }, 100);
  };

  useEffect(() => {
    // targetState와 isSlideModalOpen의 값이 다르면, Modal Open / Close 발동 조건
    if (targetState !== isSlideModalOpen) {
      if (targetState === true) {
        handleModalOpen();
      } else {
        handleModalClose();
      }
    }
  }, [targetState, isSlideModalOpen, handleModalOpen, handleModalClose]);

  return (
    <DimmedBackground
      isShow={isSlideModalOpen}
      onClick={() => {
        handleModalClose();
        if (handleCloseButtonClick) {
          handleCloseButtonClick();
        }
      }}
    >
      <SlideModalContainer
        modalPositionY={modalPositionY}
        onClick={(e) => e.stopPropagation()}
        style={{
          height,
          maxHeight,
          borderRadius: hasRoundBorder ? '24px 24px 0px 0px' : '0px',
          gap,
          padding,
        }}
      >
        {isShowCloseButton && (
          <SlideModalCloseButtonWrap>
            <button
              onClick={() => {
                handleModalClose();
                if (handleCloseButtonClick) {
                  handleCloseButtonClick();
                }
              }}
            >
              <Icon name={'CloseGrey'} width={20} height={20} />
            </button>
          </SlideModalCloseButtonWrap>
        )}
        {children}
      </SlideModalContainer>
    </DimmedBackground>
  );
};

const SlideModalContainer = styled.div<{
  modalPositionY: string;
}>`
  position: fixed;
  bottom: 0px;

  width: 100%;
  max-width: 512px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  padding: 24px 24px 32px 24px;

  gap: 32px;

  background: #fff;

  left: 50%;

  transform: ${(props) => `translate(-50%, ${props.modalPositionY})`};
  -webkit-transform: ${(props) => `translate(-50%, ${props.modalPositionY})`};

  transition: all 300ms ease;
  -webkit-transition: all 300ms ease;
`;

const SlideModalCloseButtonWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;

  padding: 24px 24px var(--radius-medium, 8px) 24px;
  gap: var(--radius-large, 16px);
`;
